import { createRoot } from "react-dom/client";
import { App } from "./App";
import { TileLibrary } from "./library/TileLibrary";
import { FortressLibrary } from "./library/FortressLibrary";
import { FortressEditor } from "./library/FortressEditor";
import { AuthPage } from "./auth/AuthPage";
import { ChooseUsernamePage } from "./auth/ChooseUsernamePage";
import { ensureValidSession } from "./auth/session";
import { getAppState } from "./appState";
import "./styles.css";

const container = document.getElementById("root");
if (!container) throw new Error('Root element "#root" not found');
const root = createRoot(container);

// Tiny path-based routing — no router dep. `/auth` is the login screen,
// `/auth/username` is the post-login handle picker, the `/library/*` pages are
// debug/catalogue tools (open), and everything else is the game, which requires
// a signed-in session with a username.
const path = window.location.pathname;

async function boot(): Promise<void> {
  if (path === "/auth/username" || path.startsWith("/auth/username/")) {
    const session = await ensureValidSession();
    if (!session) {
      const returnTo = encodeURIComponent(
        new URLSearchParams(window.location.search).get("returnTo") || "/",
      );
      window.location.replace(`/auth?returnTo=${returnTo}`);
      return;
    }
    if (session.user.username) {
      const param = new URLSearchParams(window.location.search).get("returnTo");
      const dest =
        param && param.startsWith("/") && !param.startsWith("//") ? param : "/";
      window.location.replace(dest);
      return;
    }
    root.render(<ChooseUsernamePage />);
    return;
  }

  if (path.startsWith("/auth")) {
    root.render(<AuthPage />);
    return;
  }
  if (path.startsWith("/library/tiles")) {
    root.render(<TileLibrary />);
    return;
  }
  if (path.startsWith("/library/fortresses/edit")) {
    root.render(<FortressEditor />);
    return;
  }
  if (path.startsWith("/library/fortresses")) {
    root.render(<FortressLibrary />);
    return;
  }

  // Server-driven boot: ask /app-state, then validate any stored JWT pair via
  // GET /auth/me (user must still exist). No usable session → /auth (email OTP).
  // AUTH_DISABLED skips the gate for local guest play only.
  const { authDisabled } = await getAppState();
  if (authDisabled) {
    root.render(<App />);
    return;
  }

  const session = await ensureValidSession();
  if (!session) {
    const returnTo = encodeURIComponent(path + window.location.search);
    window.location.replace(`/auth?returnTo=${returnTo}`);
    return;
  }

  if (!session.user.username) {
    const returnTo = encodeURIComponent(path + window.location.search);
    window.location.replace(`/auth/username?returnTo=${returnTo}`);
    return;
  }

  // NOTE: React.StrictMode is intentionally omitted. Its double-invocation of
  // effects in development conflicts with PixiJS's async Application.init()
  // lifecycle, which would otherwise create two canvases on mount.
  root.render(<App authenticated />);
}

void boot();
